import { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import { SortLabelButton } from "../../ui/SortLabelButton";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import type {
  LinkItem,
  LinkSortField,
  LinkStatusFilter,
  SortDirection,
} from "./useStatisticDetail";
import { deriveLinkStatus } from "./useStatisticDetail";
import { IndexingBadge } from "../../ui/IndexingBadge";

type TabDataState =
  | { kind: "loading" }
  | { kind: "empty" }
  | { kind: "data"; items: LinkItem[]; totalCount: number }
  | { kind: "error"; message: string };

interface LinksTableProps {
  state: TabDataState;
  fetching: boolean;
  page: number;
  pageSize: number;
  search: string;
  statusFilter: LinkStatusFilter;
  sortField: LinkSortField;
  sortDirection: SortDirection;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSearchChange: (search: string) => void;
  onStatusFilterChange: (status: LinkStatusFilter) => void;
  onSortChange: (field: LinkSortField) => void;
  onRefetch: () => void;
}

const STATUS_FILTER_OPTIONS: { value: LinkStatusFilter; label: string }[] = [
  { value: "all", label: "Все" },
  { value: "valid", label: "Активные" },
  { value: "invalid", label: "Исчезнувшие" },
  { value: "unchecked", label: "Не проверялись" },
];

const DEBOUNCE_MS = 400;

const LINK_TYPE_LABELS: Record<LinkItem["type"], string> = {
  article: "Статья",
  homepage: "Главная",
  sitewide: "Sitewide",
};

// Заголовок сортируемой колонки таблицы ссылок.
interface SortableColumn {
  field: LinkSortField;
  label: string;
  // Отсутствует у текстовых колонок: выравнивание задают только числовым, где
  // разряды должны стоять друг под другом.
  align?: "right";
}

export function LinksTable(props: LinksTableProps) {
  const {
    state,
    fetching,
    page,
    pageSize,
    statusFilter,
    sortField,
    sortDirection,
    onPageChange,
    onPageSizeChange,
    onSearchChange,
    onStatusFilterChange,
    onSortChange,
    onRefetch,
  } = props;

  const [localSearch, setLocalSearch] = useState(props.search);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSearchChangeRef = useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;

  useEffect(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      onSearchChangeRef.current(localSearch);
    }, DEBOUNCE_MS);
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, [localSearch]);

  const sortableColumns: SortableColumn[] = [
    { field: "anchorText", label: "Анкор" },
    { field: "publishedUrl", label: "URL статьи" },
    { field: "siteUrl", label: "Сайт-донор" },
    { field: "publishedAt", label: "Дата публикации" },
  ];

  // MUI считает страницы с нуля, экран — с единицы.
  const zeroBasedPage = page - 1;

  return (
    <Box>
      <Box mb={2} display="flex" gap={1} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          label="Поиск по анкору или URL"
          value={localSearch}
          onChange={(event) => setLocalSearch(event.target.value)}
          sx={{ width: 300 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Статус</InputLabel>
          <Select
            value={statusFilter}
            label="Статус"
            onChange={(event) => onStatusFilterChange(event.target.value)}
          >
            {STATUS_FILTER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {fetching && <LinearProgress sx={{ mb: 1 }} />}

      {state.kind === "loading" && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {state.kind === "error" && (
        <Alert
          severity="error"
          action={<Button size="small" onClick={onRefetch}>Повторить</Button>}
        >
          Ошибка загрузки: {state.message}
        </Alert>
      )}

      {state.kind === "empty" && (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">Нет ссылок</Typography>
        </Box>
      )}

      {state.kind === "data" && (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {sortableColumns.map((col) => (
                    <TableCell key={col.field} align={col.align}>
                      <SortLabelButton
                        active={sortField === col.field}
                        direction={sortField === col.field ? sortDirection : "asc"}
                        onClick={() => onSortChange(col.field)}
                      >
                        {col.label}
                      </SortLabelButton>
                    </TableCell>
                  ))}
                  <TableCell>Тип</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Последняя проверка</TableCell>
                  <TableCell align="center">Индексация</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.items.map((item, index) => {
                  const status = deriveLinkStatus({ isValid: item.isValid, lastCheckAt: item.lastCheckAt });
                  return (
                    <TableRow key={`${item.publishedUrl}-${index}`} hover>
                      <TableCell>{item.anchorText}</TableCell>
                      <TableCell>{item.publishedUrl}</TableCell>
                      <TableCell>{item.siteUrl}</TableCell>
                      <TableCell>{new Date(item.publishedAt).toLocaleString()}</TableCell>
                      <TableCell>{LINK_TYPE_LABELS[item.type]}</TableCell>
                      <TableCell>{`${status.icon} ${status.label}`}</TableCell>
                      <TableCell>
                        {item.lastCheckAt !== null
                          ? new Date(item.lastCheckAt).toLocaleString()
                          : "—"}
                      </TableCell>
                      <TableCell align="center"><IndexingBadge aggregate={item.indexing} /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={state.totalCount}
            page={zeroBasedPage}
            onPageChange={(_event, newPage) => onPageChange(newPage + 1)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(event) => onPageSizeChange(Number(event.target.value))}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="На странице:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
          />
        </>
      )}
    </Box>
  );
}
